// Extra buyer-supplied product fields, rendered by snippets/product-options.liquid.
//
// Two jobs: block add to cart until required fields are answered, and submit the
// product together with any fee lines the buyer triggered, in one request.
// Dawn's product-form.js is left alone and still handles every product that has
// no option set.
//
// Blocking is belt and braces. The add-to-cart button is held disabled while any
// required field is unanswered, which is what the buyer sees, and a capture-phase
// submit listener refuses the add regardless, which is what actually guarantees
// it. The button alone would not be enough: it is enabled between first paint and
// this deferred script running, and disabled attributes are trivially removed.
//
// A disabled button cannot be clicked, so every message this file shows on submit
// is out of reach for an ordinary buyer, and a dimmed button on its own explains
// nothing. Two things carry the reason instead: a required field shows its own
// error as soon as it is left empty, and one line beside the button says why the
// button is dead, in words, referenced from the button so assistive technology
// reads it out when the button is reached.

// A length priced field carries its rungs as JSON, ascending by letter count,
// each rung { letters, id, price, label }. `label` is the amount already
// formatted by Liquid, so no money formatting ever happens here. A field with a
// missing or malformed ladder charges nothing rather than guessing a price.
function ladderFor(field) {
  const raw = field.dataset.feeLadder;
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((rung) => rung && rung.id && Number.isFinite(Number(rung.letters)))
      .map((rung) => ({ letters: Number(rung.letters), id: String(rung.id), label: rung.label, total: rung.total }))
      .sort((a, b) => a.letters - b.letters);
  } catch (error) {
    return [];
  }
}

// Letters, not characters: whitespace does not count, so 'Anna og Jon' is 9.
// Iterated by code point rather than by code unit so that the count and the
// trimming below can never disagree about how long the value is.
function letterCount(value) {
  return Array.from(value).filter((character) => !/\s/.test(character)).length;
}

// Below the lowest rung pays the lowest rung. Past the highest cannot normally
// happen, because typing beyond it is trimmed, so the top rung is the fallback.
function rungFor(ladder, letters) {
  if (!ladder.length) return null;
  const wanted = Math.max(letters, ladder[0].letters);
  return ladder.find((rung) => rung.letters >= wanted) || ladder[ladder.length - 1];
}

class ProductOptions extends HTMLElement {
  connectedCallback() {
    if (!this.listening) {
      this.listening = true;

      // The pristine server render is the wording an emptied field returns to, so
      // it is kept before anything can overwrite it.
      this.querySelectorAll('[data-fee-live]').forEach((live) => {
        live.feeDefaultHtml = live.innerHTML;
      });

      // One delegated pair covers every control: `change` for pickers, `input`
      // for typing, and both for browsers that only fire one of them.
      const handler = (event) => this.onFieldChange(event);
      this.addEventListener('input', handler);
      this.addEventListener('change', handler);

      // Leaving a required field empty marks that field, so a buyer tabbing
      // through the buy box is shown which one is missing without having to
      // click a button that is disabled. focusout rather than blur, because
      // blur does not bubble and a delegated listener would never see it.
      this.addEventListener('focusout', (event) => this.onFieldBlur(event));

      // Turning a paid add-on on or off.
      this.addEventListener('click', (event) => {
        const toggle = event.target.closest ? event.target.closest('[data-addon-toggle]') : null;
        if (toggle) this.toggleAddon(toggle.closest('[data-option-field]'));
      });
    }

    // The server renders every add-on switched off, and the field's own
    // presentation has to agree with that from the first paint.
    this.fields.filter((field) => 'addon' in field.dataset).forEach((field) => this.renderAddon(field, false));

    // The one disable Dawn issues without a label is the variant fetch, so it
    // cannot be read off the button and is remembered here instead. Subscribed
    // to the same event product-info.js acts on, and the publish in global.js
    // VariantSelects runs every subscriber inside the change event, so the flag
    // is set in the same synchronous turn that starts the fetch: there is no
    // moment where the fetch is running and the flag is not yet set.
    if (!this.variantFetchUnsubscriber) {
      this.variantFetchUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (payload) =>
        this.onOptionValueSelectionChange(payload)
      );
    }

    this.refreshSubmitState();
    this.refreshTotal();
    this.observeProductForm();
  }

  /*
   * The price at the top of the page, showing what the order comes to rather
   * than what the product costs before anything is added to it. A buyer who has
   * switched engraving on should not have to add it up themselves, and should
   * not meet a different number at the checkout.
   *
   * Every total was formatted by Liquid, one per rung of the fee ladder: the
   * shop's money format is a Liquid setting with its own separators, and this
   * one prints ISK as 79.000 kr where a JavaScript formatter following the
   * standard rules would print 79,000. The script only picks between strings it
   * was handed and never does arithmetic on money.
   *
   * Which means the totals belong to the variant they were rendered for. A
   * product with more than one variant would go stale the moment the buyer
   * switched between them, so on those the price is left exactly as Dawn wrote
   * it. No product that charges for an option has variants today; the guard is
   * there so that the day one does, it under-promises rather than lies.
   */
  refreshTotal() {
    if (this.dataset.singleVariant !== 'true') return;

    const price = document.getElementById(`price-${this.dataset.sectionId}`);
    const amount = price ? price.querySelector('.price-item--regular') : null;
    if (!amount) return;

    // The server's own rendering is what an order with no add-ons costs, and
    // what the price returns to when they are all switched off again. Only kept
    // if there is something there: capturing an empty element and writing it
    // back later would erase the price rather than restore it.
    if (typeof this.priceAsRendered !== 'string' && amount.textContent.trim() !== '') {
      this.priceAsRendered = amount.innerHTML;
    }
    if (typeof this.priceAsRendered !== 'string') return;

    let total = null;

    this.fields.forEach((field) => {
      const answer = this.answerFor(field);
      if (answer === '') return;

      const ladder = ladderFor(field);
      if (ladder.length) {
        const rung = rungFor(ladder, letterCount(answer));
        if (rung && rung.total) total = rung.total;
      } else if (field.dataset.feeTotal) {
        total = field.dataset.feeTotal;
      }
    });

    amount.innerHTML = total || this.priceAsRendered;
  }

  // Dawn's product-info.js resetProductFormState disables the button for the
  // whole duration of the variant fetch and passes no text, and
  // product-form.js toggleSubmitButton only writes a label when it is given
  // one, so throughout that fetch the button is disabled while still reading
  // "add to cart". Without this flag the label check below would conclude Dawn
  // is not blocking, and answering the last required field mid-fetch would
  // remove Dawn's disable while the form still carries the previous variant id,
  // which is not corrected until updateVariantInputs runs on the response. The
  // add would then succeed, on the wrong variant, with nothing shown.
  //
  // Scoped to this element's own product-info, the same containment test
  // product-info.js uses: a change in another product's selectors starts no
  // fetch for our button, and flagging it would leave our button disabled with
  // nothing on the way to clear it.
  onOptionValueSelectionChange(payload) {
    const data = payload ? payload.data : null;
    const changed = data && data.event ? data.event.target : null;
    const productInfo = this.closest('product-info');
    if (!changed || !productInfo || !productInfo.contains(changed)) return;

    this.variantFetchPending = true;
    this.refreshSubmitState();
  }

  // Watches the product form so the disabled state can be re-derived whenever
  // Dawn has touched the button.
  //
  // Dawn does not replace the buy-buttons subtree on a variant change: its
  // product-info.js mutates the existing button in place. What it always does is
  // write the button's label through product-form.js toggleSubmitButton, and
  // assigning textContent replaces that span's text node, which is a childList
  // mutation this observer sees. That text write is the trigger, and it is the
  // same signal refreshSubmitState reads to decide whether Dawn is blocking, so
  // the two belong together: stop writing the label and this reapply goes quiet.
  // Whole-subtree replacement does happen on a product swap, and is covered too,
  // because the replacement brings a fresh product-options element with it.
  //
  // childList only, never attributes: refreshSubmitState writes the button's
  // disabled attribute, and observing attributes would loop.
  //
  // The same label write is what ends the variant fetch as far as this file is
  // concerned, so the in-flight flag is cleared from here. Only a mutation of
  // the label itself counts, not any mutation inside the form: the dynamic
  // checkout button lives in this subtree and renders on its own schedule, and
  // clearing on that would hand back a live button in the middle of a fetch,
  // which is the whole failure this flag exists to prevent.
  observeProductForm() {
    if (this.observer) return;

    const productForm = this.productForm;
    if (!productForm || !('MutationObserver' in window)) return;

    this.observer = new MutationObserver((records) => {
      if (this.variantFetchPending && this.labelWasWritten(records)) this.variantFetchPending = false;
      this.refreshSubmitState();
    });
    this.observer.observe(productForm, { childList: true, subtree: true });
  }

  // True when one of these mutation records is Dawn writing the button's label.
  // Assigning textContent replaces the span's text node, so the record's target
  // is the span; a wholesale replacement of the span shows up as the new span in
  // a parent's addedNodes. With no span to compare against, false: the flag
  // stays set and the button stays disabled, which is the safe answer, and a
  // buy button with no label span is already fatal to Dawn's own toggle.
  labelWasWritten(records) {
    const button = this.submitButton;
    const label = button ? button.querySelector('span') : null;
    if (!label) return false;

    return records.some(
      (record) => record.target === label || Array.prototype.includes.call(record.addedNodes, label)
    );
  }

  // Everything this element listens to stops when it leaves the document: the
  // form observer and the variant-change subscription. A replaced copy has no
  // business writing anything, and a live subscription would hold it alive.
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;

    if (this.variantFetchUnsubscriber) this.variantFetchUnsubscriber();
    this.variantFetchUnsubscriber = null;
  }

  // Once removed, this element must stop resolving a form. On a combined-listing
  // swap HTMLUpdateUtility suffixes every id and form attribute inside the
  // outgoing markup and leaves the incoming copy holding the original ids, so a
  // removed copy's data-form-id, a data attribute that was never suffixed, still
  // resolves through getElementById to the live replacement form. It would write
  // the live button's disabled state from answers nobody can see any more. This
  // makes every getter below inert instead.
  //
  // It does not cover the 500ms before removal: HTMLUpdateUtility only sets
  // display:none on the outgoing markup, so isConnected is still true for that
  // whole window. Nothing calls in during it. The fields cannot be reached
  // inside a hidden subtree, the option subscription ignores changes outside its
  // own product-info and the outgoing copy's product-info contains none of the
  // incoming controls, and the observer is watching the outgoing form, which
  // nothing writes to any more.
  get productForm() {
    if (!this.isConnected) return null;

    const form = document.getElementById(this.dataset.formId);
    return form ? form.closest('product-form') : null;
  }

  get submitButton() {
    const productForm = this.productForm;
    return productForm ? productForm.querySelector('[type="submit"]') : null;
  }

  // True while any required field is still unanswered.
  get hasUnanswered() {
    return this.fields.some(
      (field) => field.dataset.optionRequired === 'true' && this.answerFor(field) === ''
    );
  }

  // Holds add to cart disabled until every required field is answered.
  //
  // The capture-phase submit guard at the bottom of this file is still the real
  // enforcement: this only makes the state visible, and a disabled button cannot
  // be trusted on its own, because it is enabled for the moment between first
  // paint and this deferred script running.
  //
  // Both reasons the button may be dead are re-derived on every pass and the
  // attribute is written from the result, so nothing here is remembered between
  // passes. Remembering our own disabling instead is what goes wrong: Dawn
  // disables the same button for reasons of its own, and once it has, a
  // remembered "we disabled this" is a lie that survives until it does damage.
  // Answering the last required field on a sold-out variant would then remove
  // Dawn's disabled attribute and hand the buyer a live add-to-cart button that
  // still reads "Sold out".
  //
  // Only the disabled attribute is written from here. aria-disabled, .loading
  // and the sold-out span belong to addWithFees, and the two mechanisms are kept
  // to disjoint attributes on purpose.
  refreshSubmitState() {
    const button = this.submitButton;
    if (!button) return;

    const unanswered = this.hasUnanswered;
    button.toggleAttribute('disabled', unanswered || this.dawnBlocks(button));
    this.refreshBlockedNotice(unanswered);
  }

  // True while Dawn is holding the button for a reason of its own: sold out, no
  // such variant, or a quantity rule the variant cannot satisfy.
  //
  // The button's own label is the signal, because Dawn rewrites it on every
  // enable and on every disable it gives a reason for, which makes it carry no
  // history: product-form.js toggleSubmitButton writes the sold-out or
  // unavailable wording when it disables and window.variantStrings.addToCart
  // when it enables, and the server render of buy-buttons.liquid agrees. The
  // label is read through the same querySelector('span') Dawn writes through, so
  // it cannot drift onto a different node. A variantChange subscription would be
  // the obvious alternative and is wrong: product-info.js returns from
  // handleUpdateProductInfo through setUnavailable before it publishes, so the
  // no-variant case would never arrive. What the label cannot carry is the
  // variant fetch, which Dawn disables for without writing any wording at all,
  // so that one window is remembered in variantFetchPending instead and read
  // first here.
  dawnBlocks(button) {
    if (this.variantFetchPending) return true;

    const label = button.querySelector('span');
    const addToCart = window.variantStrings ? window.variantStrings.addToCart : null;

    // theme.liquid defines variantStrings on every page, and Dawn's own
    // product-info.js would throw without it, so this is only about not being
    // the first thing to throw: with no wording to compare against, fall back to
    // our own reason alone rather than guessing at Dawn's.
    if (!label || !addToCart) return false;

    return label.textContent.trim() !== addToCart.trim();
  }

  // The line that says why add to cart is dead, and the button's reference to
  // it. Shown only while a required field is unanswered, since that is the only
  // reason this file blocks; Dawn's own reasons are already written on the
  // button. aria-describedby is what carries it to a screen reader, which would
  // otherwise reach the button and find nothing but a disabled control.
  refreshBlockedNotice(unanswered) {
    const notice = this.querySelector('[data-options-blocked]');
    if (!notice || !notice.id) return;

    notice.toggleAttribute('hidden', !unanswered);

    const button = this.submitButton;
    if (!button) return;

    // Additive: whatever else describes the button keeps describing it.
    const described = (button.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== notice.id);
    if (unanswered) described.push(notice.id);

    if (described.length) button.setAttribute('aria-describedby', described.join(' '));
    else button.removeAttribute('aria-describedby');
  }

  get fields() {
    return Array.from(this.querySelectorAll('[data-option-field]'));
  }

  onFieldChange(event) {
    const field = event.target.closest ? event.target.closest('[data-option-field]') : null;
    if (!field) return;

    this.enforceLength(field);
    this.refreshFee(field);
    this.refreshLetters(field);
    this.refreshTotal();
    if (this.answerFor(field) !== '') this.markField(field, false);
    this.refreshSubmitState();
  }

  // A paid add-on starts off. While it is off its input is disabled, which keeps
  // it out of the form's submission and out of the fee calculation without any
  // separate flag to keep in step, and takes it out of the tab order so a buyer
  // never lands inside a box they have not opened.
  toggleAddon(field) {
    if (!field) return;
    this.renderAddon(field, !field.classList.contains('is-on'));
  }

  renderAddon(field, on) {
    field.classList.toggle('is-on', on);

    const toggle = field.querySelector('[data-addon-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', String(on));

    const input = field.querySelector('input[type="text"]');
    if (input) {
      input.disabled = !on;
      // Switching it off discards what was typed, so a buyer cannot be charged
      // for wording that is no longer on screen.
      if (!on) input.value = '';
    }

    this.refreshFee(field);
    this.refreshLetters(field);
    this.refreshTotal();
    this.refreshSubmitState();

    if (on && input) input.focus();
  }

  // The letter meter and the engraved preview, both driven by what is typed.
  // Neither exists on a field without them, so both calls fall through quietly.
  refreshLetters(field) {
    const answer = this.answerFor(field);

    const preview = field.querySelector('[data-engraving-preview]');
    if (preview) {
      preview.classList.toggle('is-empty', answer === '');
      preview.textContent = answer === '' ? preview.dataset.placeholder || '' : answer;
    }

    // A free engraving shows only how much of its limit is used.
    const counter = field.querySelector('[data-letters-count] [data-letters-used]');
    if (counter) counter.textContent = letterCount(answer);

    const meter = field.querySelector('[data-letters-meter]');
    if (!meter) return;

    const ladder = ladderFor(field);
    const included = Number(field.dataset.lettersIncluded) || 0;
    const used = letterCount(answer);

    meter.querySelectorAll('[data-tick]').forEach((tick) => {
      const index = Number(tick.dataset.tick);
      // Four states, so the boundary is visible before it is reached: reached
      // and free, reached and charged, still to come within the included run,
      // and still to come beyond it.
      tick.className = index <= used ? (index <= included ? 'is-used' : 'is-charged') : index <= included ? 'is-free' : '';
    });

    const usedLabel = meter.querySelector('[data-letters-used]');
    const template = meter.dataset.lettersLabel;
    if (usedLabel && template) usedLabel.textContent = template.replace('__COUNT__', used);

    const cost = meter.querySelector('[data-letters-cost]');
    if (cost) {
      const rung = used === 0 ? null : rungFor(ladder, used);
      cost.innerHTML = rung && rung.label ? rung.label : '';
    }
  }

  // A required field left empty is marked as soon as focus leaves it, through
  // the same marking validate() uses. Nothing is marked on the way in, so a
  // buyer is never told off for a field they have not reached yet.
  //
  // Switching to another window or tab also fires focusout, and that is not a
  // buyer leaving the field: they would come back to a red error on the field
  // they were still filling in. document.hasFocus() is false exactly then, and
  // true for every move inside the page, so it is the test. event.relatedTarget
  // being null is the other candidate and is wrong here: it is also null when a
  // buyer clicks any part of the page that cannot take focus, which is a real
  // abandonment that should be marked.
  onFieldBlur(event) {
    const field = event.target.closest ? event.target.closest('[data-option-field]') : null;
    if (!field || field.dataset.optionRequired !== 'true') return;
    if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;

    this.markField(field, this.answerFor(field) === '');
  }

  // What the buyer entered for one field, or '' when unanswered.
  answerFor(field) {
    switch (field.dataset.optionType) {
      case 'checkbox': {
        const box = field.querySelector('input[type="checkbox"]');
        return box && box.checked ? box.value : '';
      }
      case 'choice': {
        const picked = field.querySelector('input[type="radio"]:checked') || field.querySelector('select');
        return picked ? picked.value.trim() : '';
      }
      default: {
        const input = field.querySelector('input[type="text"]');
        return input ? input.value.trim() : '';
      }
    }
  }

  // The top rung is the cap, and because spaces do not count towards it a plain
  // maxlength cannot express it. Trim the value to the longest prefix that still
  // fits, so what the buyer typed up to that point survives.
  enforceLength(field) {
    const ladder = ladderFor(field);
    if (!ladder.length) return;

    const cap = ladder[ladder.length - 1].letters;
    const input = field.querySelector('input[type="text"]');
    if (!input || cap <= 0 || letterCount(input.value) <= cap) return;

    const characters = Array.from(input.value);
    let letters = 0;
    let cut = characters.length;
    for (let i = 0; i < characters.length; i += 1) {
      if (!/\s/.test(characters[i])) letters += 1;
      if (letters === cap) {
        cut = i + 1;
        break;
      }
    }

    const caret = input.selectionStart;
    input.value = characters.slice(0, cut).join('');
    if (typeof caret === 'number') {
      const position = Math.min(caret, input.value.length);
      try {
        input.setSelectionRange(position, position);
      } catch (error) {
        // Some input types refuse selection ranges; the trim still stands.
      }
    }
  }

  // Shows what the current length costs, using the label Liquid formatted. An
  // empty field goes back to the server-rendered "from" wording, because there
  // is no length to price yet; anything typed shows that rung's exact price with
  // no prefix, since a floor is the wrong claim once the price is exact.
  //
  // innerHTML rather than textContent because a shop's money format may carry
  // markup, which is how Liquid put the starting phrase there in the first place.
  refreshFee(field) {
    const live = field.querySelector('[data-fee-live]');
    if (!live) return;

    const ladder = ladderFor(field);
    if (!ladder.length) return;

    const answer = this.answerFor(field);
    const rung = answer === '' ? null : rungFor(ladder, letterCount(answer));

    if (!rung || !rung.label) {
      if (typeof live.feeDefaultHtml === 'string') live.innerHTML = live.feeDefaultHtml;
      return;
    }

    live.innerHTML = rung.label;
  }

  // Shows or hides one field's error and marks its controls. The single place
  // that decides what "this field is missing" looks like, so the three callers,
  // typing, leaving a field, and submitting, cannot drift apart.
  markField(field, missing) {
    const error = field.querySelector('[data-option-error]');
    if (error) error.toggleAttribute('hidden', !missing);

    field.querySelectorAll('input, select').forEach((el) => {
      if (missing) el.setAttribute('aria-invalid', 'true');
      else el.removeAttribute('aria-invalid');
    });
  }

  // Marks every unanswered required field and focuses the first one.
  // Returns true when nothing is missing.
  validate() {
    let firstMissing = null;

    this.fields.forEach((field) => {
      const missing = field.dataset.optionRequired === 'true' && this.answerFor(field) === '';
      this.markField(field, missing);

      if (missing && !firstMissing) firstMissing = field;
    });

    if (!firstMissing) return true;

    const focusable = firstMissing.querySelector('input, select');
    if (focusable) focusable.focus();
    return false;
  }

  // The fee variant this field's answer triggers, or null for none: an empty
  // field is never charged, and a charged field with neither a flat variant nor
  // a usable ladder behaves as free.
  feeVariantFor(field) {
    if (field.dataset.optionType === 'choice') return null;

    const answer = this.answerFor(field);
    if (answer === '') return null;

    const ladder = ladderFor(field);
    if (ladder.length) {
      const rung = rungFor(ladder, letterCount(answer));
      return rung ? rung.id : null;
    }

    return field.dataset.feeVariant || null;
  }

  // Fee lines the current answers trigger, as variant id -> how many fields
  // charge it. Two engravings of the same length share one variant and become a
  // single line of quantity 2; different lengths are different variants, so they
  // stay two lines of quantity 1.
  triggeredFees() {
    const counts = new Map();

    this.fields.forEach((field) => {
      const variantId = this.feeVariantFor(field);
      if (!variantId) return;
      counts.set(variantId, (counts.get(variantId) || 0) + 1);
    });

    return counts;
  }
}

if (!customElements.get('product-options')) {
  customElements.define('product-options', ProductOptions);
}

// Mirrors Dawn's handleErrorMessage: called with no message it hides the
// wrapper, which is how a stale error from a previous attempt gets cleared at
// the start of the next one. data-hide-errors is honoured, as Dawn does.
function handleFormError(productForm, message) {
  if (!productForm || productForm.dataset.hideErrors === 'true') return;

  const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
  if (!wrapper) return;

  wrapper.toggleAttribute('hidden', !message);
  const target = wrapper.querySelector('.product-form__error-message');
  if (target) target.textContent = message || '';
}

// Builds the /cart/add payload: the product with its properties, then one line
// per triggered fee. Everything from one submit shares a _set token so
// cart-line-groups.js can keep the fee tied to what it belongs to.
function buildItems(form, options) {
  const data = new FormData(form);
  const quantity = parseInt(data.get('quantity'), 10) || 1;
  const fees = options.triggeredFees();

  const properties = {};
  data.forEach((value, key) => {
    const match = key.match(/^properties\[(.+)\]$/);
    if (!match) return;
    if (String(value).trim() === '') return;
    properties[match[1]] = value;
  });

  const items = [];

  if (fees.size === 0) {
    items.push({ id: data.get('id'), quantity, properties });
    return items;
  }

  const token = `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  items.push({ id: data.get('id'), quantity, properties: { ...properties, _set: token } });

  fees.forEach((count, variantId) => {
    items.push({
      id: variantId,
      quantity: quantity * count,
      properties: { _set: token, _fee: 'true', _fee_per: String(count) },
    });
  });

  return items;
}

async function addWithFees(form, options) {
  const productForm = form.closest('product-form');
  const submitButton = productForm ? productForm.querySelector('[type="submit"]') : null;
  const spinner = productForm ? productForm.querySelector('.loading__spinner') : null;
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  if (submitButton) {
    if (submitButton.getAttribute('aria-disabled') === 'true') return;
    submitButton.setAttribute('aria-disabled', true);
    submitButton.classList.add('loading');
  }
  if (spinner) spinner.classList.remove('hidden');
  handleFormError(productForm);

  const body = { items: buildItems(form, options) };
  const variantId = body.items.length ? body.items[0].id : null;
  if (cart) {
    body.sections = cart.getSectionsToRender().map((section) => section.id);
    body.sections_url = window.location.pathname;
    cart.setActiveElement(document.activeElement);
  }

  // Only a sold-out product may leave the button disabled, exactly as Dawn does.
  // Every other failure, including a thrown one, has to stay retryable: the
  // entry guard above refuses to run while aria-disabled is set, so latching on
  // a network blip would kill add to cart until the page is reloaded.
  let soldOut = false;
  try {
    const response = await fetch(window.routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    const parsed = await response.json();

    if (parsed.status) {
      const fallback = window.cartStrings ? window.cartStrings.error : '';
      handleFormError(productForm, parsed.description || parsed.message || fallback);

      const soldOutMessage = submitButton ? submitButton.querySelector('.sold-out-message') : null;
      if (soldOutMessage) {
        soldOut = true;
        const buttonText = submitButton.querySelector('span');
        if (buttonText) buttonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
      }
      return;
    }

    if (!cart) {
      window.location = window.routes.cart_url;
      return;
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'product-options',
      productVariantId: variantId,
      cartData: parsed,
    });
    cart.renderContents(parsed);
  } catch (error) {
    console.error(error);
  } finally {
    if (submitButton) {
      submitButton.classList.remove('loading');
      if (!soldOut) submitButton.removeAttribute('aria-disabled');
    }
    if (spinner) spinner.classList.add('hidden');
    if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
  }
}

// Dawn's product-form listens for submit on the form itself. A capture-phase
// listener on document runs before that and stops the event from reaching it,
// which is how this takes over add to cart without touching product-form.js.
// Products with no option set never get here.
//
// Registered once per page. The snippet emits this script tag from inside
// itself, so a second options block, or the snippet rendered twice, would
// otherwise install a second identical listener: stopPropagation does not stop a
// sibling listener on the same node in the same phase, which would double the
// add. Guarded the same way the custom element definition above is.
if (!window.productOptionsSubmitListener) {
  window.productOptionsSubmitListener = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches || !form.matches('[data-type="add-to-cart-form"]')) return;

      // getAttribute('id'), never form.id. HTMLFormElement is specified with
      // [LegacyOverrideBuiltIns], so a form control whose name matches a built-in
      // property shadows that property, and Dawn's product form contains
      // <input name="id"> for the variant. form.id therefore returns that input
      // element rather than the form's id, the selector below becomes
      // data-form-id="[object HTMLInputElement]", nothing matches, and this
      // listener silently hands every add straight to Dawn: no validation of
      // required fields and no fee lines.
      const formId = form.getAttribute('id');
      const options = formId
        ? document.querySelector(`product-options[data-form-id="${formId}"]`)
        : null;
      if (!options) return;

      event.preventDefault();
      event.stopPropagation();

      if (!options.validate()) return;
      addWithFees(form, options);
    },
    true
  );
}
